import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    Button,
    Spinner,
    Badge,
    Form,
    Table,
    Alert,
    Dropdown
} from "react-bootstrap";
import helper from "../common/helper";
import * as constants from "../../constants/CONSTANT";
import { formatCurrency, formatDate } from "../common/formatters";
import ResizableTable from "../common/ResizableTable";

const BudgetReport = ({ permissions }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [viewMode, setViewMode] = useState("table");
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState({
        date_from: "",
        date_to: "",
        type: "all"
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [selectedRows, setSelectedRows] = useState([]);

    const allowExport = permissions?.allowExport;

    // Define columns for ResizableTable
    const columns = [
        {
            field: "date",
            label: "Date",
            searchable: true,
            align: "center",
            render: (val) => formatDate(val)
        },
        {
            field: "type",
            label: "Type",
            searchable: true,
            align: "center",
            render: (val) => (
                <Badge bg={
                    val === 'purchase' ? 'danger' :
                        val === 'penalty' ? 'success' :
                            val === 'damage' ? 'warning' : 'secondary'
                }>
                    {val}
                </Badge>
            )
        },
        {
            field: "reference",
            label: "Reference",
            searchable: true,
            align: "center",
            render: (val, row) => {
                if (row.type === 'penalty' && val.includes('Penalty:')) {
                    return <span className="text-primary fw-semibold">{val}</span>;
                }
                if (row.type === 'purchase' && val.includes('Purchase:')) {
                    return <span className="text-info fw-semibold">{val}</span>;
                }
                if (row.type === 'damage' && (val.includes('Damage:') || val.includes('Damage Book #'))) {
                    return <span className="text-warning fw-semibold">{val}</span>;
                }
                if (row.type === 'lost' && (val.includes('Lost:') || val.includes('Lost Book #'))) {
                    return <span className="text-danger fw-semibold">{val}</span>;
                }
                return <span className="text-muted">{val}</span>;
            }
        },
        {
            field: "description",
            label: "Description",
            searchable: true,
            align: "left"
        },
        {
            field: "penalty_amount",
            label: "Penalty (IN)",
            searchable: false,
            align: "center",
            render: (val) => val > 0 ? (
                <span className="text-success">+{formatReportCurrency(val)}</span>
            ) : '-'
        },
        {
            field: "amount",
            label: "Amount (OUT)",
            searchable: false,
            align: "center",
            render: (val) => val > 0 ? (
                <span className="text-danger">-{formatReportCurrency(val)}</span>
            ) : '-'
        },

        // {
        //     field: "net_amount",
        //     label: "Net Amount",
        //     searchable: false,
        //     align: "center",
        //     render: (val, row) => {
        //         const netAmount = (row.penalty_amount || 0) - (row.amount || 0);
        //         return (
        //             <span className={netAmount >= 0 ? 'text-success' : 'text-danger'}>
        //                 {netAmount >= 0 ? '+' : ''}{formatReportCurrency(netAmount)}
        //             </span>
        //         );
        //     }
        // }
    ];

    // Handle selection change
    const handleSelectionChange = (selectedItems) => {
        setSelectedRows(selectedItems);
    };

    const formatReportCurrency = (amount) => formatCurrency(amount, reportData?.summary?.currencySymbol || '₹');

    // Debug log when reportData is available
    if (reportData?.summary) {
        console.log("📊 Budget Report Debug:", {
            openingBudget: reportData.summary.openingBudget,
            totalPurchases: reportData.summary.totalPurchases,
            currencySymbol: reportData.summary.currencySymbol
        });
    }

    useEffect(() => {
        fetchBudgetReport();
    }, [filters]);

    const fetchBudgetReport = async () => {
        console.log("working");
        try {
            setLoading(true);
            setError("");

            const queryParams = new URLSearchParams();
            if (filters.date_from) queryParams.append('date_from', filters.date_from);
            if (filters.date_to) queryParams.append('date_to', filters.date_to);
            if (filters.type !== 'all') queryParams.append('type', filters.type);

            const response = await helper.fetchWithAuth(
                `${constants.API_BASE_URL}/api/budget-report?${queryParams}`,
                "GET"
            );
            if (!response.ok) {
                throw new Error('Failed to fetch budget report');
            }

            const result = await response.json();
            console.log("result =>>>", result)

            if (result.success) {
                setReportData(result.data);
            } else {
                throw new Error(result.error || 'Failed to fetch budget report');
            }
        } catch (err) {
            console.error("Error fetching budget report:", err);
            setError(err.message || "Failed to fetch budget report");
            setReportData(null);
        } finally {
            setLoading(false);
        }
    };

    // Export Functions
    const prepareExportData = () => {
        if (!reportData?.details) return [];

        return reportData.details.map(item => ({
            'Date': formatDate(item.date),
            'Type': item.type,
            'Reference': item.reference,
            'Description': item.description,
            'Amount (OUT)': item.amount || 0,
            'Penalty (IN)': item.penalty_amount || 0,
            'Net Amount': (item.penalty_amount || 0) - (item.amount || 0)
        }));
    };

    const exportToCSV = () => {
        const data = prepareExportData();
        if (data.length === 0) return alert("No data to export");

        const headers = Object.keys(data[0]).join(",");
        const rows = data.map(row =>
            Object.values(row).map(value =>
                typeof value === 'string' && value.includes(',') ? `"${value}"` : value
            ).join(",")
        );

        const csvContent = [headers, ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `budget_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.click();
    };

    const exportToExcel = () => {
        // Simple HTML table export that Excel can open
        const data = prepareExportData();
        if (data.length === 0) return alert("No data to export");

        let html = `
            <html>
            <head>
                <style>
                    table { border-collapse: collapse; width: 100%; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; font-weight: bold; }
                    .summary { margin-bottom: 20px; }
                    .summary td { font-weight: bold; background-color: #f9f9f9; }
                </style>
            </head>
            <body>
                <h2>Budget Report - ${new Date().toLocaleDateString()}</h2>
                
                <div class="summary">
                    <h3>Summary</h3>
                    <table>
                        <tr><td>Metric</td><td>Amount</td></tr>
                        <tr><td>Opening Budget</td><td>${reportData.summary.openingBudget}</td></tr>
                        <tr><td>Total Purchases</td><td>${-reportData.summary.totalPurchases}</td></tr>
                        <tr><td>Total Penalties</td><td>${reportData.summary.totalPenalties}</td></tr>
                        <tr><td>Total Losses</td><td>${-reportData.summary.totalLosses}</td></tr>
                        <tr><td>Closing Budget</td><td>${reportData.summary.closingBudget}</td></tr>
                        <tr><td>Net Change</td><td>${reportData.summary.netChange}</td></tr>
                    </table>
                </div>
                
                <h3>Details</h3>
                <table>
        `;

        // Add headers
        html += "<tr>";
        Object.keys(data[0]).forEach(key => {
            html += `<th>${key}</th>`;
        });
        html += "</tr>";

        // Add data rows
        data.forEach(row => {
            html += "<tr>";
            Object.values(row).forEach(value => {
                html += `<td>${value}</td>`;
            });
            html += "</tr>";
        });

        html += `
                </table>
            </body>
            </html>
        `;

        const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", `budget_report_${new Date().toISOString().split('T')[0]}.xls`);
        link.click();
    };

    const exportToPDF = () => {
        // Simple print-to-PDF functionality
        const data = prepareExportData();
        if (data.length === 0) return alert("No data to export");

        let html = `
            <html>
            <head>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    h1 { color: #333; }
                    h2 { color: #666; margin-top: 30px; }
                    table { border-collapse: collapse; width: 100%; margin-bottom: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; font-weight: bold; }
                    .summary { margin-bottom: 30px; }
                    .summary td { font-weight: bold; background-color: #f9f9f9; }
                    .positive { color: green; }
                    .negative { color: red; }
                    @media print { body { margin: 10px; } }
                </style>
            </head>
            <body>
                <h1>Budget Report</h1>
                <p>Generated on: ${new Date().toLocaleDateString()}</p>
                
                <div class="summary">
                    <h2>Summary</h2>
                    <table>
                        <tr><td>Metric</td><td>Amount</td></tr>
                        <tr><td>Opening Budget</td><td>${formatReportCurrency(reportData.summary.openingBudget)}</td></tr>
                        <tr><td>Total Purchases</td><td class="negative">-${formatReportCurrency(reportData.summary.totalPurchases)}</td></tr>
                        <tr><td>Total Penalties</td><td class="positive">+${formatReportCurrency(reportData.summary.totalPenalties)}</td></tr>
                        <tr><td>Total Losses</td><td class="negative">-${formatReportCurrency(reportData.summary.totalLosses)}</td></tr>
                        <tr><td>Closing Budget</td><td>${formatReportCurrency(reportData.summary.closingBudget)}</td></tr>
                        <tr><td>Net Change</td><td class="${reportData.summary.netChange >= 0 ? 'positive' : 'negative'}">${reportData.summary.netChange >= 0 ? '+' : ''}${formatReportCurrency(reportData.summary.netChange)}</td></tr>
                    </table>
                </div>
                
                <h2>Transaction Details</h2>
                <table>
        `;

        // Add headers
        html += "<tr>";
        Object.keys(data[0]).forEach(key => {
            html += `<th>${key}</th>`;
        });
        html += "</tr>";

        // Add data rows
        data.forEach(row => {
            html += "<tr>";
            Object.values(row).forEach(value => {
                html += `<td>${value}</td>`;
            });
            html += "</tr>";
        });

        html += `
                </table>
                
                <script>
                    window.onload = function() {
                        window.print();
                    }
                </script>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
    };

    const getBudgetStatusColor = (amount) => {
        if (amount > 0) return 'success';
        if (amount < 0) return 'danger';
        return 'warning';
    };

    return (
        <>
            <div className="container-fluid" style={{ marginTop: "-14px" }}>
                <Card className="border-0 shadow-sm">
                    <Card.Header
                        className="border shadow-sm mt-4 rounded mx-2 custom-card-header"
                    >
                        <Row className="align-items-center px-3">
                            {/* LEFT SECTION */}
                            <Col md={6} className="d-flex align-items-center gap-3">
                                {/* Back Button */}
                                <button
                                    onClick={() => navigate('/reports')}
                                    className="shadow-sm d-flex align-items-center justify-content-center custom-btn-back"
                                >
                                    <i className="fa-solid fa-arrow-left"></i>
                                </button>

                                {/* Title */}
                                <div>
                                    <h4 className="mb-0 fw-bold" style={{ color: "var(--primary-color)" }}>
                                        Budget Report
                                    </h4>
                                    <p className="mb-0 text-muted small">
                                        Comprehensive budget analysis including purchases, penalties, damages, and lost books
                                    </p>
                                </div>
                            </Col>

                            {/* RIGHT SECTION */}
                            <Col md={6} className="text-end">
                                <Dropdown align="end">
                                    <Dropdown.Toggle variant="light" size="sm" id="options-dropdown">
                                        <i className="fa fa-bars me-1" /> Options
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="shadow-sm border-0 mt-2">
                                        <Dropdown.Header className="small text-uppercase fw-bold text-muted">
                                            View Mode
                                        </Dropdown.Header>

                                        <Dropdown.Item
                                            active={viewMode === 'table'}
                                            onClick={() => setViewMode('table')}
                                        >
                                            <i className="fa fa-table me-2" /> Table
                                        </Dropdown.Item>

                                        <Dropdown.Item
                                            active={viewMode === 'dashboard'}
                                            onClick={() => setViewMode('dashboard')}
                                        >
                                            <i className="fa fa-chart-pie me-2" /> Dashboard
                                        </Dropdown.Item>

                                        <Dropdown.Item
                                            active={viewMode === 'chart'}
                                            onClick={() => setViewMode('chart')}
                                        >
                                            <i className="fa fa-chart-bar me-2" /> Analytics
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        <Dropdown.Header className="small text-uppercase fw-bold text-muted">
                                            Export Options
                                        </Dropdown.Header>

                                        <Dropdown.Item onClick={exportToExcel}>
                                            <i className="fa-solid fa-file-excel me-2 text-success" /> Excel
                                        </Dropdown.Item>

                                        <Dropdown.Item onClick={exportToCSV}>
                                            <i className="fa-solid fa-file-csv me-2 text-info" /> CSV
                                        </Dropdown.Item>

                                        <Dropdown.Item onClick={exportToPDF}>
                                            <i className="fa-solid fa-file-pdf me-2 text-danger" /> PDF
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </Col>
                        </Row>
                    </Card.Header>

                    <Card.Body className="">
                        {/* Filters */}
                        <Card className="mb-4 border-0 shadow-sm">
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>From Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={filters.date_from}
                                                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>To Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={filters.date_to}
                                                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Transaction Type</Form.Label>
                                            <Form.Select
                                                value={filters.type}
                                                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                                            >
                                                <option value="all">All Types</option>
                                                <option value="purchase">Purchases Only</option>
                                                <option value="penalty">Penalties Only</option>
                                                <option value="damage">Damages Only</option>
                                                <option value="lost">Lost Books Only</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")}>
                                {error}
                            </Alert>
                        )}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2">Loading budget report...</p>
                            </div>
                        ) : reportData ? (
                            <>
                                {viewMode === 'table' && (
                                    <>


                                        {(reportData.summary.totalDamageLosses > 0 || reportData.summary.totalLostLosses > 0) && (
                                            <Row >
                                                {/* <Col md={6}>
                                                    <Card className="border-0 shadow-sm">
                                                        <Card.Header className="bg-white border-bottom">
                                                            <h5 className="mb-0">💔 Losses Breakdown</h5>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            <Row>
                                                                <Col>
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <span>📚 Damaged Books:</span>
                                                                        <span className="text-danger">-{formatReportCurrency(reportData.summary.totalDamageLosses)}</span>
                                                                    </div>
                                                                    <small className="text-muted">{reportData.counts.damageCount} items</small>
                                                                </Col>
                                                                <Col>
                                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                                        <span>🔴 Lost Books:</span>
                                                                        <span className="text-danger">-{formatReportCurrency(reportData.summary.totalLostLosses)}</span>
                                                                    </div>
                                                                    <small className="text-muted">{reportData.counts.lostCount} items</small>
                                                                </Col>
                                                            </Row>
                                                            <hr />
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <strong>Total Losses:</strong>
                                                                <strong className="text-danger">-{formatReportCurrency(reportData.summary.totalLosses)}</strong>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col> */}
                                                {/* <Col md={6}>
                                                    <Card className="border-0 shadow-sm">
                                                        <Card.Header className="bg-white border-bottom">
                                                            <h5 className="mb-0">📈 Budget Summary</h5>
                                                        </Card.Header>
                                                        <Card.Body>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span>Total Income:</span>
                                                                <span className="text-success">+{formatReportCurrency(reportData.summary.totalIncome)}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span>Total Expenses:</span>
                                                                <span className="text-danger">-{formatReportCurrency(reportData.summary.totalExpenses)}</span>
                                                            </div>
                                                            <hr />
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <strong>Net Change:</strong>
                                                                <strong className={getBudgetStatusColor(reportData.summary.netChange)}>
                                                                    {reportData.summary.netChange >= 0 ? '+' : ''}{formatReportCurrency(reportData.summary.netChange)}
                                                                </strong>
                                                            </div>
                                                        </Card.Body>
                                                    </Card>
                                                </Col> */}
                                            </Row>
                                        )}

                                        {/* Detailed Transactions */}
                                        <Row>
                                            <Col md={12}>
                                                <Card className="border-0 shadow-sm">
                                                    <Row>
                                                        <Col lg={4}>
                                                            <Card.Header className="bg-white border-bottom">
                                                                <h5 style={{ color: "var(--primary-color)" }}> Detailed Transactions</h5>

                                                            </Card.Header>
                                                        </Col>

                                                        {/* Summary Cards - Ek line mein 3 cards */}
                                                        <Col lg={8} md={12}>
                                                            <Row className="g-2">
                                                                <Col lg={4} md={4} sm={4} xs={12}>
                                                                    <Card className="border-0 shadow-sm h-100">
                                                                        <Card.Body className="p-1 text-center">
                                                                            <h5 className="mb-1 ">-{formatReportCurrency(reportData.summary.totalPurchases)}</h5>
                                                                            <p className="mb-0 small">Total Purchases</p>
                                                                            <small className="text-muted">{reportData.counts.purchaseCount} transactions</small>
                                                                        </Card.Body>
                                                                    </Card>
                                                                </Col>
                                                                <Col lg={4} md={4} sm={4} xs={12}>
                                                                    <Card className="border-0 shadow-sm h-100">
                                                                        <Card.Body className="p-1 text-center">
                                                                            <h5 className="mb-1 text-success">+{formatReportCurrency(reportData.summary.totalPenalties)}</h5>
                                                                            <p className="mb-0 small">Total Penalties</p>
                                                                            <small className="text-muted">{reportData.counts.penaltyCount} transactions</small>
                                                                        </Card.Body>
                                                                    </Card>
                                                                </Col>

                                                                <Col lg={4} md={4} sm={4} xs={12}>
                                                                    <Card className="border-0 shadow-sm h-100">
                                                                        <Card.Body className="p-1 text-center">
                                                                            <h5 className="mb-1 text-warning">-{formatReportCurrency(reportData.summary.totalLosses)}</h5>
                                                                            <p className="mb-0 small">Total Losses</p>
                                                                            <small className="text-muted">{reportData.counts.damageCount + reportData.counts.lostCount} items</small>
                                                                        </Card.Body>
                                                                    </Card>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                    </Row>

                                                    <Card.Body>
                                                        <Row>
                                                            {/* Table - Full width */}
                                                            <Col lg={12} md={12}>
                                                                <div className="table-responsive">
                                                                    <ResizableTable
                                                                        data={reportData.details}
                                                                        columns={columns}
                                                                        loading={loading}
                                                                        currentPage={currentPage}
                                                                        recordsPerPage={rowsPerPage}
                                                                        onPageChange={setCurrentPage}
                                                                        showSerialNumber={true}
                                                                        showCheckbox={true}
                                                                        showActions={false}
                                                                        selectedItems={selectedRows}
                                                                        onSelectionChange={handleSelectionChange}
                                                                    />
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                        {/* <Row className="g-3">
                                            <Col md={12}>
                                                <Card className="border-0 shadow-sm">
                                                    <Row>
                                                        <Col lg={3}>

                                                            <Card className="border-0 shadow-sm">
                                                                <Card.Body className="p-3 text-center">
                                                                    <h5 className="mb-1 text-danger">-{formatReportCurrency(reportData.summary.totalPurchases)}</h5>
                                                                    <p className="mb-0 small">Total Purchases</p>
                                                                    <small className="text-muted">{reportData.counts.purchaseCount} transactions</small>
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col md={12}>
                                                            <Card className="border-0 shadow-sm">
                                                                <Card.Body className="p-3 text-center">
                                                                    <h5 className="mb-1 text-success">+{formatReportCurrency(reportData.summary.totalPenalties)}</h5>
                                                                    <p className="mb-0 small">Total Penalties</p>
                                                                    <small className="text-muted">{reportData.counts.penaltyCount} transactions</small>
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                        <Col md={12}>
                                                            <Card className="border-0 shadow-sm">
                                                                <Card.Body className="p-3 text-center">
                                                                    <h5 className="mb-1 text-warning">-{formatReportCurrency(reportData.summary.totalLosses)}</h5>
                                                                    <p className="mb-0 small">Total Losses</p>
                                                                    <small className="text-muted">{reportData.counts.damageCount + reportData.counts.lostCount} items</small>
                                                                </Card.Body>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                    <Card.Header className="bg-white border-bottom">
                                                        <h5 className="mb-0">📋 Detailed Transactions</h5>
                                                        <small className="text-muted">
                                                            {reportData.details.length} transactions found
                                                        </small>
                                                    </Card.Header>


                                                    <Card.Body>
                                                        <div className="table-responsive">
                                                            <ResizableTable
                                                                data={reportData.details}
                                                                columns={columns}
                                                                loading={loading}
                                                                currentPage={currentPage}
                                                                recordsPerPage={rowsPerPage}
                                                                onPageChange={setCurrentPage}
                                                                showSerialNumber={true}
                                                                showCheckbox={true}
                                                                showActions={false}
                                                                selectedItems={selectedRows}
                                                                onSelectionChange={handleSelectionChange}
                                                            />
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row> */}
                                    </>
                                )}

                                {viewMode === 'dashboard' && (
                                    <Row className="g-4">
                                        <Col md={8}>
                                            <Card className="border-0 shadow-sm h-100">
                                                <Card.Header className="bg-white border-bottom">
                                                    <h6 className="mb-0 fw-bold">Budget Overview</h6>
                                                </Card.Header>
                                                <Card.Body className="d-flex align-items-center justify-content-center">
                                                    <div className="text-center">
                                                        <h2 className="display-4 fw-bold text-primary">
                                                            {formatReportCurrency(reportData.summary.closingBudget)}
                                                        </h2>
                                                        <p className="text-muted">Current Budget Balance</p>
                                                        <div className="mt-3">
                                                            <Badge bg={getBudgetStatusColor(reportData.summary.netChange)} className="p-2">
                                                                {reportData.summary.netChange >= 0 ? '+' : ''}{formatReportCurrency(reportData.summary.netChange)} Net Change
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col md={4}>
                                            <Card className="border-0 shadow-sm h-100">
                                                <Card.Header className="bg-white border-bottom">
                                                    <h6 className="mb-0 fw-bold">Quick Stats</h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span>📚 Purchases</span>
                                                            <span className="text-danger">-{formatReportCurrency(reportData.summary.totalPurchases)}</span>
                                                        </div>
                                                        <small className="text-muted">{reportData.counts.purchaseCount} transactions</small>
                                                    </div>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span>💰 Penalties</span>
                                                            <span className="text-success">+{formatReportCurrency(reportData.summary.totalPenalties)}</span>
                                                        </div>
                                                        <small className="text-muted">{reportData.counts.penaltyCount} penalties</small>
                                                    </div>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <span>💔 Losses</span>
                                                            <span className="text-danger">-{formatReportCurrency(reportData.summary.totalLosses)}</span>
                                                        </div>
                                                        <small className="text-muted">{reportData.counts.damageCount + reportData.counts.lostCount} items</small>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}

                                {viewMode === 'chart' && (
                                    <Row className="g-4">
                                        <Col md={12}>
                                            <Card className="border-0 shadow-sm">
                                                <Card.Header className="bg-white border-bottom">
                                                    <h6 className="mb-0 fw-bold">Analytics View</h6>
                                                </Card.Header>
                                                <Card.Body className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                                                    <div className="text-center">
                                                        <i className="fa fa-chart-bar fa-3x text-muted mb-3"></i>
                                                        <h5 className="text-muted">Analytics Charts Coming Soon</h5>
                                                        <p className="text-muted">Budget analytics and charts will be available in the next update</p>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-5">
                                <p className="text-muted">No budget data available</p>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </>
    );
};

export default BudgetReport;
